import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    App as AntdApp,
    Button,
    Form,
    Input,
    Modal,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
} from "antd";
import type { TableColumnsType } from "antd";
import {
    FileSearchOutlined,
    PlusOutlined,
    ReloadOutlined,
    StopOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import {
    buildPaginatedListPagination,
    PaginatedListCard,
    PaginatedListContainer,
    PaginatedListToolbar,
    PAGINATED_LIST_TABLE_SIZE,
} from "../../../components/common/PaginatedListTable";
import { usePermissions } from "../../../hooks/usePermissions";
import {
    cancelModelEvaluationTask,
    createModelEvaluationTask,
    getModelEvaluationTasks,
    rerunModelEvaluationTask,
} from "../../../services/modelManagementService";
import {
    getValidationOptions,
    listValidationDatasets,
    listValidationRules,
} from "../../../services/modelValidationService";
import { localeService } from "../../../services/localeService";
import type { ModelEvaluationTaskSummary } from "../../../types/modelManagement";
import type {
    ValidationDataset,
    ValidationModelVersionOption,
    ValidationRule,
} from "../../../types/modelValidation";
import { MODEL_EVALUATE_ACCESS_PERMISSIONS } from "../../../utils/permissions";
import { ModelEvaluationReportContent } from "../ModelEvaluationReportPage";
import { getModelManagementErrorDetail } from "../modelManagement/pageConfig";
import { getCurrentPublishedValidationVersion } from "./modelValidationUtils";
import { EvaluationTaskConfigurationSummary } from "./ModelEvaluationTaskConfiguration";

const RUNNING_STATUSES = new Set(["queued", "running"]);
const REPORTABLE_STATUSES = new Set(["succeeded"]);
const RERUNNABLE_STATUSES = new Set(["failed", "cancelled", "expired"]);
const statusColors: Record<string, string> = {
    cancelled: "default",
    expired: "default",
    failed: "error",
    queued: "processing",
    running: "processing",
    succeeded: "success",
};
const recommendationColors: Record<string, string> = {
    manual_review: "warning",
    not_recommended: "error",
    recommended: "success",
};

const PAGE_SIZE = 10;

interface V3EvaluationFormValues {
    baselineInsectVersionId?: number | null;
    baselineMosquitoVersionId: number;
    candidateInsectVersionId?: number | null;
    candidateMosquitoVersionId: number;
    datasetStableId: string;
    name?: string;
    ruleStableId: string;
}

const ModelEvaluationV3Section: React.FC = () => {
    const { message } = AntdApp.useApp();
    const { t } = useTranslation(["admin", "common"]);
    const { hasAnyPermissions } = usePermissions();
    const canEvaluate = hasAnyPermissions(MODEL_EVALUATE_ACCESS_PERMISSIONS);
    const [form] = Form.useForm<V3EvaluationFormValues>();
    const [tasks, setTasks] = useState<ModelEvaluationTaskSummary[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [datasets, setDatasets] = useState<ValidationDataset[]>([]);
    const [rules, setRules] = useState<ValidationRule[]>([]);
    const [versions, setVersions] = useState<ValidationModelVersionOption[]>(
        [],
    );
    const [loading, setLoading] = useState(false);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [actionTaskId, setActionTaskId] = useState<number | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [reportTaskId, setReportTaskId] = useState<number | null>(null);
    const [rerunTask, setRerunTask] =
        useState<ModelEvaluationTaskSummary | null>(null);

    const loadTasks = useCallback(
        async ({ silent = false }: { silent?: boolean } = {}) => {
            if (!silent) setLoading(true);
            try {
                const response = await getModelEvaluationTasks({
                    limit: pageSize,
                    offset: (page - 1) * pageSize,
                });
                setTasks(response.items);
                setTotal(response.total);
            } catch (error) {
                if (!silent) {
                    message.error(
                        getModelManagementErrorDetail(
                            error,
                            t("admin:modelValidation.messages.loadTasksFailed"),
                            t,
                        ),
                    );
                }
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [message, page, pageSize, t],
    );

    const loadCreateOptions = useCallback(async () => {
        if (!canEvaluate) return;
        setOptionsLoading(true);
        try {
            const [datasetResponse, ruleResponse, optionResponse] =
                await Promise.all([
                    listValidationDatasets({ limit: 100 }),
                    listValidationRules({ limit: 100 }),
                    getValidationOptions(),
                ]);
            setDatasets(datasetResponse.items);
            setRules(ruleResponse.items);
            setVersions(optionResponse.model_versions);
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t("admin:modelValidation.messages.loadOptionsFailed"),
                    t,
                ),
            );
        } finally {
            setOptionsLoading(false);
        }
    }, [canEvaluate, message, t]);

    useEffect(() => {
        void loadTasks();
    }, [loadTasks]);

    useEffect(() => {
        void loadCreateOptions();
    }, [loadCreateOptions]);

    useEffect(() => {
        if (!tasks.some((task) => RUNNING_STATUSES.has(task.status))) {
            return undefined;
        }
        const timer = window.setInterval(
            () => void loadTasks({ silent: true }),
            5000,
        );
        return () => window.clearInterval(timer);
    }, [loadTasks, tasks]);

    const usableVersions = useMemo(
        () =>
            versions.filter((version) =>
                ["validated", "active", "disabled"].includes(version.status),
            ),
        [versions],
    );
    const modelOptions = (modelKey: string) =>
        usableVersions
            .filter((version) => version.model_key === modelKey)
            .map((version) => ({
                value: version.id!,
                label: version.version_label,
            }));

    const datasetOptions = datasets.flatMap((dataset) => {
        const version = getCurrentPublishedValidationVersion(
            dataset.versions,
            dataset.latest_published_version,
        );
        return version
            ? [
                  {
                      value: dataset.stable_id,
                      label: dataset.name,
                  },
              ]
            : [];
    });
    const ruleOptions = rules.flatMap((rule) => {
        const version = getCurrentPublishedValidationVersion(
            rule.versions,
            rule.latest_published_version,
        );
        return version
            ? [
                  {
                      value: rule.stable_id,
                      label: rule.name,
                  },
              ]
            : [];
    });

    const openCreateDialog = () => {
        setCreateOpen(true);
        void loadCreateOptions();
    };

    const handleCreate = async () => {
        const values = await form.validateFields();
        setCreating(true);
        try {
            await createModelEvaluationTask({
                name: values.name?.trim() || null,
                projectId: null,
                mappingSetId: "default",
                validationDatasetStableId: values.datasetStableId,
                validationRuleStableId: values.ruleStableId,
                baselinePipeline: {
                    mosquitoVersionId: values.baselineMosquitoVersionId,
                    insectVersionId: values.baselineInsectVersionId ?? null,
                },
                candidatePipeline: {
                    mosquitoVersionId: values.candidateMosquitoVersionId,
                    insectVersionId: values.candidateInsectVersionId ?? null,
                },
            });
            message.success(t("admin:modelValidation.messages.taskCreated"));
            setCreateOpen(false);
            form.resetFields();
            await loadTasks();
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t("admin:modelValidation.messages.createTaskFailed"),
                    t,
                ),
            );
        } finally {
            setCreating(false);
        }
    };

    const handleTaskAction = async (
        taskId: number,
        action: () => Promise<unknown>,
        successMessage?: string,
    ): Promise<boolean> => {
        setActionTaskId(taskId);
        try {
            await action();
            await loadTasks();
            if (successMessage) message.success(successMessage);
            return true;
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t("admin:modelValidation.messages.operationFailed"),
                    t,
                ),
            );
            return false;
        } finally {
            setActionTaskId(null);
        }
    };

    const confirmRerun = (record: ModelEvaluationTaskSummary) => {
        setRerunTask(record);
    };

    const handleRerun = async () => {
        if (!rerunTask) return;
        const succeeded = await handleTaskAction(
            rerunTask.id,
            () => rerunModelEvaluationTask(rerunTask.id),
            t("admin:modelValidation.messages.taskRerunStarted"),
        );
        if (succeeded) setRerunTask(null);
    };

    const columns: TableColumnsType<ModelEvaluationTaskSummary> = [
        {
            title: t("admin:modelValidation.columns.task"),
            dataIndex: "name",
            width: 190,
            render: (value: string, record) =>
                REPORTABLE_STATUSES.has(record.status) ? (
                    <Button
                        type="link"
                        onClick={() => setReportTaskId(record.id)}
                    >
                        {value}
                    </Button>
                ) : (
                    value
                ),
        },
        {
            title: t("admin:modelValidation.columns.status"),
            dataIndex: "status",
            width: 140,
            render: (value: string, record) => {
                const statusLabel = t(
                    `admin:modelValidation.taskStatus.${value}`,
                );
                const total = Math.max(record.requested_sample_count || 0, 0);
                const completed = Math.min(
                    total,
                    Math.max(record.processed_sample_count || 0, 0) +
                        Math.max(record.failed_sample_count || 0, 0),
                );
                const label = RUNNING_STATUSES.has(value)
                    ? t("admin:modelValidation.taskStatus.progress", {
                          status: statusLabel,
                          completed,
                          total,
                      })
                    : statusLabel;
                return <Tag color={statusColors[value] || "default"}>{label}</Tag>;
            },
        },
        {
            title: t("admin:modelValidation.columns.configuration"),
            width: 430,
            render: (_, record) => (
                <EvaluationTaskConfigurationSummary task={record} />
            ),
        },
        {
            title: t("admin:modelValidation.columns.createdAt"),
            dataIndex: "created_at",
            width: 160,
            render: (value?: string | null) =>
                localeService.formatDateTime(value),
        },
        {
            title: t("admin:modelValidation.columns.recommendation"),
            dataIndex: "recommendation",
            fixed: "right",
            width: 110,
            render: (value?: string | null) =>
                value ? (
                    <Tag color={recommendationColors[value] || "default"}>
                        {t(`admin:modelValidation.recommendation.${value}`)}
                    </Tag>
                ) : (
                    "-"
                ),
        },
        {
            title: t("admin:modelValidation.columns.actions"),
            fixed: "right",
            width: 120,
            render: (_, record) => (
                <Space size="small">
                    {REPORTABLE_STATUSES.has(record.status) ? (
                        <Tooltip
                            title={t(
                                "admin:modelValidation.actions.viewReport",
                            )}
                        >
                            <Button
                                aria-label={t(
                                    "admin:modelValidation.actions.viewReport",
                                )}
                                icon={<FileSearchOutlined />}
                                onClick={() => setReportTaskId(record.id)}
                            />
                        </Tooltip>
                    ) : null}
                    {canEvaluate && RUNNING_STATUSES.has(record.status) ? (
                        <Tooltip
                            title={t(
                                "admin:modelValidation.actions.cancelTask",
                            )}
                        >
                            <Button
                                aria-label={t(
                                    "admin:modelValidation.actions.cancelTask",
                                )}
                                danger
                                icon={<StopOutlined />}
                                loading={actionTaskId === record.id}
                                onClick={() =>
                                    void handleTaskAction(record.id, () =>
                                        cancelModelEvaluationTask(record.id),
                                    )
                                }
                            />
                        </Tooltip>
                    ) : null}
                    {canEvaluate && RERUNNABLE_STATUSES.has(record.status) ? (
                        <Tooltip
                            title={t("admin:modelValidation.actions.rerun")}
                        >
                            <Button
                                aria-label={t(
                                    "admin:modelValidation.actions.rerun",
                                )}
                                icon={<ReloadOutlined />}
                                loading={actionTaskId === record.id}
                                onClick={() => confirmRerun(record)}
                            />
                        </Tooltip>
                    ) : null}
                </Space>
            ),
        },
    ];

    return (
        <PaginatedListContainer>
            <PaginatedListToolbar>
                {canEvaluate ? (
                    <Button
                        aria-label={t("common:buttons.add")}
                        icon={<PlusOutlined />}
                        onClick={openCreateDialog}
                        type="primary"
                    >
                        {t("common:buttons.add")}
                    </Button>
                ) : null}
                <Button
                    icon={<ReloadOutlined />}
                    loading={loading}
                    onClick={() => void loadTasks()}
                >
                    {t("admin:modelValidation.actions.refresh")}
                </Button>
            </PaginatedListToolbar>
            <PaginatedListCard>
                <Table
                    columns={columns}
                    dataSource={tasks}
                    loading={loading}
                    pagination={buildPaginatedListPagination({
                        current: page,
                        pageSize,
                        total,
                        showTotal: (count) =>
                            t("admin:modelValidation.pagination.totalRecords", {
                                total: count,
                            }),
                        onChange: (nextPage, nextPageSize) => {
                            const resolvedPageSize = nextPageSize ?? pageSize;
                            setPage(
                                resolvedPageSize !== pageSize ? 1 : nextPage,
                            );
                            setPageSize(resolvedPageSize);
                        },
                    })}
                    rowKey="id"
                    scroll={{ x: 1150 }}
                    size={PAGINATED_LIST_TABLE_SIZE}
                />
            </PaginatedListCard>

            <Modal
                destroyOnClose
                footer={null}
                maskClosable={false}
                onCancel={() => setReportTaskId(null)}
                open={reportTaskId !== null}
                styles={{
                    body: {
                        maxHeight: "calc(100dvh - 160px)",
                        overflowX: "hidden",
                        overflowY: "auto",
                    },
                }}
                title={t("admin:modelValidation.report.title")}
                width="min(1440px, calc(100vw - 24px))"
            >
                {reportTaskId !== null ? (
                    <ModelEvaluationReportContent
                        embedded
                        taskId={reportTaskId}
                    />
                ) : null}
            </Modal>

            <Modal
                cancelButtonProps={{
                    "aria-label": t("common:buttons.cancel"),
                    disabled: actionTaskId === rerunTask?.id,
                }}
                cancelText={t("common:buttons.cancel")}
                confirmLoading={actionTaskId === rerunTask?.id}
                destroyOnClose
                maskClosable={false}
                onCancel={() => setRerunTask(null)}
                onOk={() => void handleRerun()}
                okButtonProps={{
                    "aria-label": t("admin:modelValidation.actions.rerun"),
                }}
                okText={t("admin:modelValidation.actions.rerun")}
                open={rerunTask !== null}
                title={t("admin:modelValidation.confirmations.rerunTaskTitle")}
                width="min(480px, calc(100vw - 24px))"
            >
                {t("admin:modelValidation.confirmations.rerunTaskDescription")}
            </Modal>

            <Modal
                cancelButtonProps={{ "aria-label": t("common:buttons.cancel") }}
                confirmLoading={creating}
                destroyOnClose
                cancelText={t("common:buttons.cancel")}
                maskClosable={false}
                onCancel={() => setCreateOpen(false)}
                onOk={() => void handleCreate()}
                okButtonProps={{ "aria-label": t("common:buttons.save") }}
                okText={t("common:buttons.save")}
                open={createOpen}
                title={t("admin:modelValidation.evaluations.createTitle")}
                width="min(860px, 94vw)"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label={t("admin:modelValidation.fields.name")}
                        name="name"
                        rules={[{ max: 128 }]}
                    >
                        <Input maxLength={128} />
                    </Form.Item>
                    <Form.Item
                        label={t("admin:modelValidation.evaluations.dataset")}
                        name="datasetStableId"
                        rules={[{ required: true }]}
                    >
                        <Select
                            loading={optionsLoading}
                            options={datasetOptions}
                            showSearch
                            optionFilterProp="label"
                        />
                    </Form.Item>
                    <Form.Item
                        label={t("admin:modelValidation.evaluations.rule")}
                        name="ruleStableId"
                        rules={[{ required: true }]}
                    >
                        <Select
                            loading={optionsLoading}
                            options={ruleOptions}
                            showSearch
                            optionFilterProp="label"
                        />
                    </Form.Item>
                    <Space align="start" size="large" wrap>
                        <div style={{ minWidth: 320 }}>
                            <Form.Item
                                label={t(
                                    "admin:modelValidation.evaluations.baselineMosquito",
                                )}
                                name="baselineMosquitoVersionId"
                                rules={[{ required: true }]}
                            >
                                <Select
                                    optionFilterProp="label"
                                    options={modelOptions("mosquito")}
                                    showSearch
                                />
                            </Form.Item>
                            <Form.Item
                                label={t(
                                    "admin:modelValidation.evaluations.baselineInsect",
                                )}
                                name="baselineInsectVersionId"
                            >
                                <Select
                                    allowClear
                                    optionFilterProp="label"
                                    options={modelOptions("insect")}
                                    showSearch
                                />
                            </Form.Item>
                        </div>
                        <div style={{ minWidth: 320 }}>
                            <Form.Item
                                label={t(
                                    "admin:modelValidation.evaluations.candidateMosquito",
                                )}
                                name="candidateMosquitoVersionId"
                                rules={[{ required: true }]}
                            >
                                <Select
                                    optionFilterProp="label"
                                    options={modelOptions("mosquito")}
                                    showSearch
                                />
                            </Form.Item>
                            <Form.Item
                                label={t(
                                    "admin:modelValidation.evaluations.candidateInsect",
                                )}
                                name="candidateInsectVersionId"
                            >
                                <Select
                                    allowClear
                                    optionFilterProp="label"
                                    options={modelOptions("insect")}
                                    showSearch
                                />
                            </Form.Item>
                        </div>
                    </Space>
                </Form>
            </Modal>
        </PaginatedListContainer>
    );
};

export default ModelEvaluationV3Section;
